import { Fragment, type ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { useGuardedNav } from '../../components/UnsavedGuard';
import {
  SETTINGS_SECTIONS,
  type SettingsGroupItems,
  type SettingsSectionKey,
  settingsFor,
} from '../../lib/nav';
import { homeOf, useRole } from '../../lib/role';
import type { AuthedPageProps } from '../../page-route';
import { AlertsSection } from './Alerts/page';
import { SiteSection } from './Site/page';
import { SitesSection } from './Sites/page';

/*
 * Экран «Настройки» (007 T037; spec.md FR-001; дизайн-система SettingsLayout, ux.md §11; прототип
 * prototypes/site-onboarding.html). Один экран из двух колонок: слева навигация разделов группами (`t-snav`, ширина
 * `size-settings-nav`), справа открыт один раздел. До 900 px навигация — горизонтальная лента над разделом без
 * заголовков групп (`bundle.css`). Каркас листа — `AppShell`: на адресах `/settings/*` лист без полей страницы.
 *
 * Разделы — вложенные адреса одной страницы (`route.ts`: `/settings/*`), их папки `Sites/`, `Site/`, `Alerts/` — без
 * своего `route.ts`: иначе `pnpm gen` сделал бы из них отдельные страницы без навигации разделов. Наполняют разделы
 * задачи T039 (список площадок), T040, T041 (страница площадки), T047 (тревоги).
 */

/**
 * Навигация разделов и открытый раздел (`t-set`). Активный раздел — ссылка с классом `on` и `aria-current="page"`
 * (`NavLink`); раздел площадки подсвечивает «Площадки». Переход — через защиту несохранённого. На телефоне пункт
 * ленты — цель нажатия 40 px (`max-sm:h-10`): в `bundle.css` у `t-snav a` 30 px на всех ширинах.
 */
export function SettingsLayout({
  groups,
  wide,
  children,
}: {
  groups: readonly SettingsGroupItems[];
  /** Раздел-список (таблица с панелью) — во всю ширину; раздел-форма — до 720 px. */
  wide?: boolean;
  children: ReactNode;
}) {
  const guarded = useGuardedNav();
  return (
    <div className="t-set flex-1">
      <nav className="t-snav" aria-label="Разделы настроек">
        {groups.map((g) => (
          // Заголовок и список — прямо в `t-snav`, как в дизайн-системе: до 900 px заголовки прячутся, пункты встают лентой.
          <Fragment key={g.group}>
            <h2 className="t-mh">{g.label}</h2>
            <ul>
              {g.sections.map((s) => (
                <li key={s.key}>
                  <NavLink
                    to={s.to}
                    onClick={guarded.link(s.to)}
                    className={({ isActive }) => `max-sm:h-10 ${isActive ? 'on' : ''}`}
                  >
                    <s.icon className="t-ico" aria-hidden />
                    {s.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </Fragment>
        ))}
      </nav>
      <div className="t-set-body">
        <div className={wide ? 't-set-in wide' : 't-set-in'}>{children}</div>
      </div>
    </div>
  );
}

/** Адрес раздела внутри `/settings/*` и его вид: список во всю ширину или форма. */
const SECTION_ROUTES: readonly {
  path: string;
  section: SettingsSectionKey;
  wide: boolean;
  element: ReactNode;
}[] = [
  { path: 'sites', section: 'sites', wide: true, element: <SitesSection /> },
  { path: 'sites/:site', section: 'sites', wide: false, element: <SiteSection /> },
  { path: 'alerts', section: 'alerts', wide: false, element: <AlertsSection /> },
];

/**
 * Страница «Настройки»: разделы роли (`settingsFor`), у каждого свой адрес. Без раздела и по неизвестному адресу —
 * первый доступный раздел. Раздел без права не открывается: первый доступный; у роли нет ни одного раздела — её
 * домашняя страница (пункта «Настройки» у неё нет).
 */
export function SettingsPage(_props: AuthedPageProps) {
  const { role } = useRole();
  const groups = settingsFor(role);
  const allowed = new Set(groups.flatMap((g) => g.sections.map((s) => s.key)));
  const first = SETTINGS_SECTIONS.find((s) => allowed.has(s.key));
  if (!first) return <Navigate to={homeOf(role)} replace />;
  const toFirst = <Navigate to={first.to} replace />;
  return (
    <Routes>
      {SECTION_ROUTES.map((r) => (
        <Route
          key={r.path}
          path={r.path}
          element={
            allowed.has(r.section) ? (
              <SettingsLayout groups={groups} wide={r.wide}>
                {r.element}
              </SettingsLayout>
            ) : (
              toFirst
            )
          }
        />
      ))}
      <Route path="*" element={toFirst} />
    </Routes>
  );
}
